/**
 * Lightweight inline toast (design-spec.md §4 — micro-feedback on mutation).
 * Not a global provider; pages render <Toast> from local state and auto-dismiss.
 * Motion gated on useReducedMotion via fadeOnly/fadeRise.
 */
import { useEffect } from 'react';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { CheckCircle2, AlertTriangle, X } from 'lucide-react';

export type ToastTone = 'success' | 'error';
export interface ToastMessage {
  id: number;
  tone: ToastTone;
  text: string;
}

export function Toast({
  toast,
  onDismiss,
}: {
  toast: ToastMessage | null;
  onDismiss: () => void;
}) {
  const reduce = useReducedMotion();

  useEffect(() => {
    if (!toast) return;
    const t = setTimeout(onDismiss, 4000);
    return () => clearTimeout(t);
  }, [toast, onDismiss]);

  const isError = toast?.tone === 'error';

  return (
    <div className="pointer-events-none fixed bottom-6 right-6 z-50">
      <AnimatePresence>
        {toast && (
          <motion.div
            key={toast.id}
            initial={reduce ? { opacity: 0 } : { opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            exit={reduce ? { opacity: 0 } : { opacity: 0, y: 12 }}
            transition={{ duration: 0.25, ease: [0.16, 1, 0.3, 1] }}
            role="status"
            aria-live="polite"
            className={`pointer-events-auto flex max-w-sm items-start gap-2.5 rounded-xl border px-4 py-3 text-sm shadow-soft ${
              isError
                ? 'border-red-100 bg-red-50 text-red-700'
                : 'border-green-100 bg-green-50 text-green-700'
            }`}
          >
            {isError ? (
              <AlertTriangle size={18} className="mt-0.5 shrink-0" aria-hidden />
            ) : (
              <CheckCircle2 size={18} className="mt-0.5 shrink-0" aria-hidden />
            )}
            <span className="flex-1">{toast.text}</span>
            <button onClick={onDismiss} aria-label="Tutup notifikasi" className="shrink-0 opacity-60 hover:opacity-100">
              <X size={16} aria-hidden />
            </button>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
